import type { GetProps } from "tamagui";
import { webPressScale } from "../interactive/styles";
import { useInteractiveElement } from "../interactive/useInteractiveElement";
import { Card, type CardProps } from "./Card";
type StyleOverrides = Pick<GetProps<typeof Card>, "borderColor" | "backgroundColor" | "hoverStyle" | "pressStyle">;
export type SelectionTone = "default" | "brand";
export type SelectionCardProps = CardProps & {
  selected?: boolean;
  /** `brand` accents selection and interaction in the brand lime. */
  tone?: SelectionTone;
};

// Static portion — applies whenever `selected`, even when non-interactive.
const selectedStaticStyles: StyleOverrides = {
  borderColor: "$borderIntense"
};

// Interactive portion — gated on `isInteractive` to avoid hover bleed on static cards.
const selectedInteractiveStyles: StyleOverrides = {
  hoverStyle: {
    backgroundColor: "$surface2Hover",
    borderColor: "$borderIntenseHover"
  },
  pressStyle: {
    backgroundColor: "$surface2Hover",
    borderColor: "$borderIntenseHover",
    ...webPressScale
  }
};

// Brand tone accents hover and press whether or not the card is selected, so an
// unselected option previews the choice; selected is the same fill, kept.
const brandStaticStyles: StyleOverrides = {
  borderColor: "$borderBrand",
  backgroundColor: "$bgBrand"
};
const brandInteractiveStyles: StyleOverrides = {
  hoverStyle: {
    backgroundColor: "$bgBrand",
    borderColor: "$borderBrandHover"
  },
  pressStyle: {
    backgroundColor: "$bgBrand",
    borderColor: "$borderBrandHover",
    ...webPressScale
  }
};
const selectedDisabledStaticStyles: StyleOverrides = {
  borderColor: "$borderStrong"
};
const selectedDisabledInteractiveStyles: StyleOverrides = {
  hoverStyle: {
    backgroundColor: "$bgDisabledSubtle",
    borderColor: "$borderStrong"
  },
  pressStyle: {
    backgroundColor: "$bgDisabledSubtle",
    borderColor: "$borderStrong",
    scale: 1
  }
};

/**
 * Card with selection-state visual variants. Composes `Card` rather than
 * extending via `styled(Card)` — variant matching needs a styled config on
 * the base, and `Card` is a plain function component.
 */
export const SelectionCard = ({
  selected = false,
  tone = "default",
  ...rest
}: SelectionCardProps) => {
  const {
    isInteractive
  } = useInteractiveElement(rest);
  const {
    pressStyle: consumerPressStyle,
    hoverStyle: consumerHoverStyle,
    borderColor: consumerBorderColor,
    backgroundColor: consumerBackgroundColor,
    ...passthrough
  } = rest;

  // Variants merge on top of consumer values per sub-key. Conditional spread
  // on the JSX side: `prop={undefined}` overrides Tamagui's styled defaults
  // rather than falling through.
  const selectedActive = selected;
  const disabledSelectedActive = selected && rest.disabled === true;
  const brandActive = tone === "brand" && rest.disabled !== true;
  const variantPressStyle = brandActive && isInteractive ? brandInteractiveStyles.pressStyle : selectedActive && isInteractive ? disabledSelectedActive ? selectedDisabledInteractiveStyles.pressStyle : selectedInteractiveStyles.pressStyle : undefined;
  const variantHoverStyle = brandActive && isInteractive ? brandInteractiveStyles.hoverStyle : selectedActive && isInteractive ? disabledSelectedActive ? selectedDisabledInteractiveStyles.hoverStyle : selectedInteractiveStyles.hoverStyle : undefined;
  const pressStyle = variantPressStyle != null || consumerPressStyle != null ? {
    ...consumerPressStyle,
    ...variantPressStyle
  } : undefined;
  const hoverStyle = variantHoverStyle != null || consumerHoverStyle != null ? {
    ...consumerHoverStyle,
    ...variantHoverStyle
  } : undefined;
  const borderColor = disabledSelectedActive ? selectedDisabledStaticStyles.borderColor : selectedActive ? brandActive ? brandStaticStyles.borderColor : selectedStaticStyles.borderColor : consumerBorderColor;
  const backgroundColor = brandActive && selectedActive ? brandStaticStyles.backgroundColor : consumerBackgroundColor;
  return <Card {...passthrough} {...pressStyle !== undefined ? {
    pressStyle
  } : {}} {...hoverStyle !== undefined ? {
    hoverStyle
  } : {}} {...borderColor !== undefined ? {
    borderColor
  } : {}} {...backgroundColor !== undefined ? {
    backgroundColor
  } : {}} data-sentry-element="Card" data-sentry-component="SelectionCard" data-sentry-source-file="SelectionCard.tsx" />;
};